import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import sumBy from 'lodash-es/sumBy';
import {
  type IClientStatementSchema,
  type IOwnerPayoutConfiguration,
  ClientStatementPaymentStatus,
  ClientStatementStatus,
  ClientStatementType,
} from 'uplisting-frontend/models/schemas';
import ClientModel from 'uplisting-frontend/models/client';
import PropertyModel from 'uplisting-frontend/models/property';
import ClientStatementExpenseModel from 'uplisting-frontend/models/client-statement-expense';
import ClientStatementReportModel from 'uplisting-frontend/models/client-statement-report';

export default class ClientStatementModel
  extends Model
  implements IClientStatementSchema
{
  @attr('string') status!: ClientStatementStatus;
  @attr('string') paymentStatus!: ClientStatementPaymentStatus;
  @attr('string') htmlVersion!: string;
  @attr('string') fileLink!: string;
  @attr('string') startDate!: string;
  @attr('string') endDate!: string;
  @attr('string') currency!: string;
  @attr('date') invoiceDate!: Date;
  @attr('string') invoiceNumber!: string;
  @attr('string') type!: ClientStatementType;
  @attr('object') ownerPayoutConfiguration!: IOwnerPayoutConfiguration;

  @hasMany('client-statement-expense', {
    inverse: 'clientStatement',
    async: false,
  })
  expenses!: ClientStatementExpenseModel[];

  @belongsTo('client', { inverse: null, async: false })
  client!: ClientModel;

  @belongsTo('property', { inverse: null, async: false })
  property!: PropertyModel;

  @belongsTo('client-statement-report', { inverse: null, async: false })
  report!: ClientStatementReportModel;

  @cached
  get isPayout(): boolean {
    return this.type === ClientStatementType.payout;
  }

  @cached
  get isInvoice(): boolean {
    return this.type === ClientStatementType.invoice;
  }

  @cached
  get isFinalised(): boolean {
    return this.status === ClientStatementStatus.finalised;
  }

  @cached
  get isUnknown(): boolean {
    return this.paymentStatus === ClientStatementPaymentStatus.unknown;
  }

  @cached
  get isUnpaid(): boolean {
    return this.paymentStatus === ClientStatementPaymentStatus.unpaid;
  }

  @cached
  get isPaid(): boolean {
    return this.paymentStatus === ClientStatementPaymentStatus.paid;
  }

  @cached
  get expensesWithTaxSum(): number {
    return sumBy(
      this.expenses.filter((expense) => !expense.isNew),
      'amountWithTax',
    );
  }

  @cached
  get reportRevenueSum(): number {
    const { report } = this;
    const { ownerPayoutConfiguration } = this;

    let base = 0;

    if (ownerPayoutConfiguration.accommodation) {
      base += report.accommodationTotalSum;
    }

    if (ownerPayoutConfiguration.extra_guest_charge) {
      base += report.extraGuestChargeSum;
    }

    if (ownerPayoutConfiguration.cleaning_fee) {
      base += report.cleaningFeeSum;
    }

    if (ownerPayoutConfiguration.extra_charges) {
      base += report.extraChargesSum;
    }

    if (ownerPayoutConfiguration.upsells) {
      base += report.upsellsSum;
    }

    if (ownerPayoutConfiguration.booking_taxes) {
      base += report.bookingTaxesSum;
    }

    if (ownerPayoutConfiguration.discount) {
      base -= report.discountSum;
    }

    return base;
  }

  @cached
  get totalDirectCostSum(): number {
    const { report } = this;
    const { ownerPayoutConfiguration } = this;

    let base = 0;

    if (ownerPayoutConfiguration.channel_commission_with_tax) {
      base += report.channelCommissionSum;
    }

    if (ownerPayoutConfiguration.payment_processing_fee) {
      base += report.paymentProcessingFeeSum;
    }

    return base;
  }

  @cached
  get managementFeeSum(): number {
    if (this.ownerPayoutConfiguration.management_fee) {
      return this.report.managementFeeSum;
    }

    return 0;
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'client-statement': ClientStatementModel;
  }
}
